import { useEffect, useState } from 'react'
import {Download, Eye, FileText, History, Pencil, Printer, Trash2, Users, X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { formatDateTimeBR, formatDateUTCBR, maskCPF, nowISO } from '@/utils/masks'
import NovoProtocoloModal from '@/components/protocolos/NovoProtocoloModal'
import { datasDoProtocolo } from '@/components/protocolos/requerimentoConfig'
import StatusBadge from '@/components/protocolos/StatusBadge'
import DocumentoViewerModal from '@/components/protocolos/DocumentoViewerModal'
import { abrirJanelaDocumento, gerarHtmlRequerimento } from '@/utils/documentoRequerimento'

interface Protocolo {
  _id: string
  numeroProtocolo: string
  nomeCompleto: string
  cpf: string
  funcaoExercida: string
  localTrabalho: string
  dataAbonada: string
  datasAbonadas?: string[]
  coletivo?: boolean
  secretariaId?: string
  subLocalId?: string
  motivo: string
  descricaoProblema?: string
  descricaoOutro?: string
  atestadoUrl?: string
  atestadoNomeArquivo?: string
  anexos?: { url: string, nomeArquivo: string, tipo?: string, tamanho?: number, enviadoPorUserId?: string, enviadoEm?: string }[]
  status: string
  parecer?: string
  cidadeId?: string
  analisadoPorUserId?: string
  analisadoPorNome?: string
  analisadoEm?: string
  excluidoEm?: string
  createdAt: string
  assinaturaAutorizador?: {
    nomeCompleto?: string
    funcao?: string
    cpf?: string
    chave?: string
  }
}

interface AnaliseProtocoloModalProps {
  protocolo: Protocolo
  onClose: () => void
  onUpdated: () => void
  podeAnalisar?: boolean
  podeExcluir?: boolean
}

const acoes = [
  { status: 'APROVADO', label: 'Aprovar' },
  { status: 'APROVADO_COM_ABONO', label: 'Aprovar com Abono' },
  { status: 'APROVADO_SEM_ABONO', label: 'Aprovar sem Abono' },
  { status: 'REPROVADO', label: 'Reprovar' },
]

function AnaliseProtocoloModal({ protocolo, onClose, onUpdated, podeAnalisar: podeAnalisarRole = true, podeExcluir = false }: AnaliseProtocoloModalProps) {
  const { user } = useAppAuth()
  const [parecer, setParecer] = useState(protocolo.parecer || '')
  const [saving, setSaving] = useState(false)
  const [excluindo, setExcluindo] = useState(false)
  const [historico, setHistorico] = useState<any[]>([])
  const [loadingHistorico, setLoadingHistorico] = useState(true)
  const [documentoAberto, setDocumentoAberto] = useState<{ url: string, nome: string } | null>(null)
  const [cidade, setCidade] = useState<any>(null)
  const [editando, setEditando] = useState(false)
  const analistaRole = user?.role === 'ADMIN' || user?.role === 'MODERADOR'
  const podeEditar = !protocolo.excluidoEm && (protocolo.status === 'AGUARDANDO_ANALISE' || analistaRole)
  const podeAnalisar = podeAnalisarRole && protocolo.status === 'AGUARDANDO_ANALISE'

  useEffect(() => {
    const fetchHistorico = async () => {
      setLoadingHistorico(true)
      try {
        const r = await (lumi.entities as any).protocolo_historico.list({
          filter: { protocoloId: protocolo.numeroProtocolo },
          sort: { createdAt: -1 },
          limit: 50,
        })
        setHistorico(r?.list || [])
      }
      catch {
        setHistorico([])
      }
      finally {
        setLoadingHistorico(false)
      }
    }
    fetchHistorico()
  }, [protocolo.numeroProtocolo])

  useEffect(() => {
    const fetchCidade = async () => {
      if (!protocolo.cidadeId) return
      try {
        const c = await (lumi.entities as any).cidades.get(protocolo.cidadeId)
        setCidade(c || null)
      }
      catch {
        setCidade(null)
      }
    }
    fetchCidade()
  }, [protocolo.cidadeId])

  const handleAcao = async (novoStatus: string, label: string) => {
    if (!parecer.trim()) {
      toast.error('O parecer é obrigatório.')
      return
    }
    setSaving(true)
    try {
      await (lumi.entities as any).protocolos.update(protocolo._id, {
        status: novoStatus,
        parecer,
        analisadoPorUserId: user?.userId || '',
        analisadoPorNome: user?.nomeCompleto || '',
        analisadoEm: nowISO(),
        updatedAt: nowISO(),
      })

      await (lumi.entities as any).protocolo_historico.create({
        protocoloId: protocolo.numeroProtocolo,
        acao: novoStatus,
        descricao: `${label}. Parecer: ${parecer}`,
        userId: user?.userId || '',
        creator: user?.userId || '',
        createdAt: nowISO(),
      })

      toast.success(`Protocolo ${label.toLowerCase()} com sucesso.`)
      onUpdated()
      onClose()
    }
    catch {
      toast.error('Erro ao processar. Tente novamente.')
    }
    finally {
      setSaving(false)
    }
  }

  const handleExcluir = async () => {
    if (!window.confirm(`Deseja mover o protocolo ${protocolo.numeroProtocolo} para a área de excluídos?`))
      return
    setExcluindo(true)
    try {
      await (lumi.entities as any).protocolos.update(protocolo._id, {
        excluido: true,
        excluidoPorUserId: user?.userId || '',
        excluidoPorNome: user?.nomeCompleto || '',
        excluidoEm: nowISO(),
        updatedAt: nowISO(),
      })

      await (lumi.entities as any).protocolo_historico.create({
        protocoloId: protocolo.numeroProtocolo,
        acao: 'EXCLUIDO',
        descricao: `Requerimento movido para a área de excluídos por ${user?.nomeCompleto || 'utilizador'}.`,
        userId: user?.userId || '',
        creator: user?.userId || '',
        createdAt: nowISO(),
      })

      toast.success('Requerimento movido para a área de excluídos.')
      onUpdated()
      onClose()
    }
    catch {
      toast.error('Erro ao excluir. Tente novamente.')
    }
    finally {
      setExcluindo(false)
    }
  }

  const handleBaixar = () => {
    const html = gerarHtmlRequerimento(protocolo, cidade)
    abrirJanelaDocumento(html, false)
  }

  const handleImprimir = () => {
    const html = gerarHtmlRequerimento(protocolo, cidade)
    abrirJanelaDocumento(html, true)
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-[600px] max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7E9EC] sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-[16px] font-semibold text-[#12141A]">
              Requerimento
              {' '}
              {protocolo.numeroProtocolo}
            </h2>
            <p className="text-[12px] text-[#9CA3AF]">{formatDateTimeBR(protocolo.createdAt)}</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[13px] text-[#6B7280]">Estado atual</span>
            <StatusBadge status={protocolo.status} comSelo />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleBaixar}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#EEF3FE] text-[#1E4FBF] text-[13px] font-medium hover:bg-[#DCE6FC] transition-colors"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Baixar Requerimento
            </button>
            <button
              type="button"
              onClick={handleImprimir}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#D1D5DB] text-[#374151] text-[13px] font-medium hover:bg-[#F5F6F8] transition-colors"
            >
              <Printer className="w-4 h-4" strokeWidth={1.75} />
              Imprimir
            </button>
            {podeEditar && (
              <button
                type="button"
                onClick={() => setEditando(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#D1D5DB] text-[#374151] text-[13px] font-medium hover:bg-[#F5F6F8] transition-colors"
              >
                <Pencil className="w-4 h-4" strokeWidth={1.75} />
                Editar
              </button>
            )}
            {podeExcluir && !protocolo.excluidoEm && (
              <button
                type="button"
                disabled={excluindo}
                onClick={handleExcluir}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-[13px] font-medium hover:bg-[#FEE2E2] transition-colors disabled:opacity-60 ml-auto"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                {excluindo ? 'Excluindo...' : 'Excluir'}
              </button>
            )}
          </div>

          {protocolo.coletivo && (
            <div className="flex items-center gap-2 rounded-xl border border-[#F3D9A4] bg-[#FFFCF4] px-4 py-2.5">
              <Users className="w-4 h-4 text-[#B7791F] shrink-0" strokeWidth={2} />
              <p className="text-[12.5px] text-[#8A6D3B]">
                <span className="font-semibold text-[#92400E]">Requerimento coletivo</span>
                {' '}
                — abrange todos os servidores do local de trabalho
                {' '}
                <span className="font-semibold text-[#92400E]">{protocolo.localTrabalho}</span>.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#F7F8FA] rounded-xl p-4">
            {!protocolo.coletivo && (
              <>
                <div>
                  <p className="text-[11.5px] text-[#9CA3AF]">Nome Completo</p>
                  <p className="text-[13.5px] text-[#12141A] font-medium">{protocolo.nomeCompleto}</p>
                </div>
                <div>
                  <p className="text-[11.5px] text-[#9CA3AF]">CPF</p>
                  <p className="text-[13.5px] text-[#12141A] font-medium">{maskCPF(protocolo.cpf)}</p>
                </div>
                <div>
                  <p className="text-[11.5px] text-[#9CA3AF]">Função</p>
                  <p className="text-[13.5px] text-[#12141A] font-medium">{protocolo.funcaoExercida}</p>
                </div>
              </>
            )}
            <div>
              <p className="text-[11.5px] text-[#9CA3AF]">Local de Trabalho</p>
              <p className="text-[13.5px] text-[#12141A] font-medium">{protocolo.localTrabalho}</p>
            </div>
            <div>
              <p className="text-[11.5px] text-[#9CA3AF]">{datasDoProtocolo(protocolo).length > 1 ? 'Datas Abonadas' : 'Data Abonada'}</p>
              <p className="text-[13.5px] text-[#12141A] font-medium">
                {datasDoProtocolo(protocolo).map(d => formatDateUTCBR(`${d}T12:00:00.000Z`)).join(' • ')}
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[11.5px] text-[#9CA3AF]">Motivo</p>
              <p className="text-[13.5px] text-[#12141A] font-medium">{protocolo.motivo}</p>
            </div>
            {protocolo.descricaoProblema && (
              <div className="sm:col-span-2">
                <p className="text-[11.5px] text-[#9CA3AF]">Descrição do Problema</p>
                <p className="text-[13.5px] text-[#12141A]">{protocolo.descricaoProblema}</p>
              </div>
            )}
            {protocolo.descricaoOutro && (
              <div className="sm:col-span-2">
                <p className="text-[11.5px] text-[#9CA3AF]">Descrição do Motivo</p>
                <p className="text-[13.5px] text-[#12141A]">{protocolo.descricaoOutro}</p>
              </div>
            )}
            {protocolo.atestadoUrl && (
              <div className="sm:col-span-2">
                <p className="text-[11.5px] text-[#9CA3AF] mb-1">Atestado Anexado</p>
                <button
                  type="button"
                  onClick={() => setDocumentoAberto({ url: protocolo.atestadoUrl as string, nome: protocolo.atestadoNomeArquivo || 'Atestado' })}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EEF3FE] text-[#1E4FBF] text-[13px] font-medium hover:bg-[#DCE6FC] transition-colors"
                >
                  <Eye className="w-4 h-4" strokeWidth={1.75} />
                  {protocolo.atestadoNomeArquivo || 'Ver atestado'}
                </button>
              </div>
            )}
            {protocolo.anexos && protocolo.anexos.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-[11.5px] text-[#9CA3AF] mb-1">
                  Outros Anexos (
                  {protocolo.anexos.length}
                  )
                </p>
                <ul className="flex flex-col gap-1.5">
                  {protocolo.anexos.map((a, idx) => (
                    <li key={a.url + '-' + idx}>
                      <button
                        type="button"
                        onClick={() => setDocumentoAberto({ url: a.url, nome: a.nomeArquivo })}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#E7E9EC] text-[#1E4FBF] text-[13px] font-medium hover:bg-[#EEF3FE] transition-colors w-full"
                      >
                        <FileText className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                        <span className="truncate">{a.nomeArquivo}</span>
                        <Eye className="w-3.5 h-3.5 ml-auto shrink-0 text-[#9CA3AF]" strokeWidth={1.75} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {!podeAnalisar && protocolo.analisadoPorNome && (
            <div className={`rounded-xl px-4 py-3 flex flex-col gap-0.5 ${protocolo.status === 'REPROVADO' ? 'bg-[#FEF2F2]' : 'bg-[#EEF3FE]'}`}>
              <p className={`text-[13px] font-medium ${protocolo.status === 'REPROVADO' ? 'text-[#B91C1C]' : 'text-[#1E4FBF]'}`}>
                {protocolo.status === 'REPROVADO' ? 'Reprovado por:' : 'Aprovado por:'}
                {' '}
                {protocolo.analisadoPorNome}
              </p>
              <p className="text-[12px] text-[#4B5563]">
                Tratado em
                {' '}
                {formatDateTimeBR(protocolo.analisadoEm)}
              </p>
            </div>
          )}

          {protocolo.assinaturaAutorizador?.nomeCompleto && (
            <div className="rounded-xl border border-[#E7E9EC] bg-white px-4 py-3">
              <p className="text-[10px] font-semibold tracking-[0.14em] text-[#1E4FBF] mb-1.5">ASSINATURA AUTORIZADORA</p>
              <p className="text-[13.5px] font-medium text-[#12141A]">{protocolo.assinaturaAutorizador.nomeCompleto}</p>
              <p className="text-[12px] text-[#4B5563]">
                {protocolo.assinaturaAutorizador.funcao ? `Função: ${protocolo.assinaturaAutorizador.funcao}` : ''}
                {protocolo.assinaturaAutorizador.cpf ? ` • CPF: ${maskCPF(protocolo.assinaturaAutorizador.cpf)}` : ''}
              </p>
              {protocolo.assinaturaAutorizador.chave && (
                <p className="text-[8.5px] tracking-[0.08em] text-[#9CA3AF] font-mono mt-1.5 break-all">
                  Chave de autenticidade: {protocolo.assinaturaAutorizador.chave}
                </p>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-2">
              <History className="w-4 h-4 text-[#6B7280]" strokeWidth={1.75} />
              <span className="text-[13px] font-medium text-[#374151]">Trilha de Auditoria</span>
            </div>
            {loadingHistorico
              ? <p className="text-[12.5px] text-[#9CA3AF]">A carregar histórico...</p>
              : historico.length === 0
                ? <p className="text-[12.5px] text-[#9CA3AF]">Sem registos de histórico.</p>
                : (
                    <ul className="flex flex-col gap-2 max-h-[220px] overflow-y-auto pr-1">
                      {historico.map(h => (
                        <li key={h._id} className="flex flex-col gap-0.5 border-l-2 border-[#1E4FBF]/30 pl-3 py-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[12.5px] font-medium text-[#12141A]">{h.acao}</span>
                            <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">{formatDateTimeBR(h.createdAt)}</span>
                          </div>
                          {h.descricao ? <p className="text-[12px] text-[#4B5563]">{h.descricao}</p> : null}
                        </li>
                      ))}
                    </ul>
                  )}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
              Parecer
              {' '}
              {podeAnalisar ? '(obrigatório)' : ''}
            </label>
            <textarea
              value={parecer}
              onChange={e => setParecer(e.target.value)}
              rows={4}
              disabled={!podeAnalisar}
              placeholder="Escreva o parecer da análise..."
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] resize-none disabled:bg-[#F7F8FA] disabled:text-[#6B7280]"
            />
          </div>

          {podeAnalisar
            ? (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  {acoes.map(a => (
                    <button
                      key={a.status}
                      disabled={saving}
                      onClick={() => handleAcao(a.status, a.label)}
                      className={`px-4 py-2.5 rounded-lg text-[13.5px] font-medium disabled:opacity-60 transition-colors ${
                        a.status === 'REPROVADO'
                          ? 'bg-[#FEF2F2] text-[#B91C1C] hover:bg-[#FEE2E2]'
                          : 'bg-[#EEF3FE] text-[#1E4FBF] hover:bg-[#DCE6FC]'
                      }`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )
            : (
                <div className="text-[13px] text-[#9CA3AF] bg-[#F7F8FA] rounded-lg px-3 py-2.5">
                  {protocolo.status === 'AGUARDANDO_ANALISE'
                    ? 'Apenas administradores ou moderadores podem analisar este protocolo.'
                    : 'Este protocolo já foi analisado e não pode ser alterado.'}
                </div>
              )}
        </div>
      </div>

      {editando && (
        <NovoProtocoloModal
          protocolo={protocolo}
          onClose={() => setEditando(false)}
          onCreated={() => {
            onUpdated()
            setEditando(false)
            onClose()
          }}
        />
      )}

      {documentoAberto && (
        <DocumentoViewerModal
          url={documentoAberto.url}
          nomeArquivo={documentoAberto.nome}
          onClose={() => setDocumentoAberto(null)}
        />
      )}
    </div>
  )
}

export default AnaliseProtocoloModal
